import {create} from 'zustand';
import {DistanceSetting, EyeToTrain, RangeSetting, TrainingMode} from '../types/exercises';

export type ExerciseConfig = {
  type: TrainingMode;
  duration: number; // in seconds
};

export type WorkoutConfig = {
  exercises: ExerciseConfig[];
  totalDuration: number;
};

export type PresetConfig = {
  name: string;
  description: string;
  exercise: TrainingMode;
  eyeToTrain: EyeToTrain;
  isMonocular: boolean;
  isRedGreen: boolean;
  isMFBF: boolean;
  speed: number;
  range: RangeSetting;
  distance: DistanceSetting;
};

export const PRESET_CONFIGS: PresetConfig[] = [
  {
    name: 'Basic Eye Tracking',
    description: 'Start here - Simple pursuit movements to build foundational tracking skills',
    exercise: 'pursuit',
    eyeToTrain: 'both',
    isMonocular: false,
    isRedGreen: false,
    isMFBF: false,
    speed: 7,
    range: 'medium',
    distance: 'middle',
  },
  {
    name: 'Convergence Training',
    description: 'Helps with eye teaming and focusing on near objects',
    exercise: 'convergence',
    eyeToTrain: 'both',
    isMonocular: false,
    isRedGreen: true,
    isMFBF: false,
    speed: 7,
    range: 'medium',
    distance: 'near',
  },
  {
    name: 'Left Eye Strengthening',
    description: 'Focused training for left eye weakness',
    exercise: 'saccades',
    eyeToTrain: 'left',
    isMonocular: true,
    isRedGreen: false,
    isMFBF: false,
    speed: 7,
    range: 'medium',
    distance: 'middle',
  },
  {
    name: 'Right Eye Strengthening',
    description: 'Focused training for right eye weakness',
    exercise: 'saccades',
    eyeToTrain: 'right',
    isMonocular: true,
    isRedGreen: false,
    isMFBF: false,
    speed: 7,
    range: 'medium',
    distance: 'middle',
  },
  {
    name: 'Advanced Eye Teaming',
    description: 'Complex movements for improved binocular vision',
    exercise: 'figure8',
    eyeToTrain: 'both',
    isMonocular: false,
    isRedGreen: true,
    isMFBF: true,
    speed: 15,
    range: 'wide',
    distance: 'middle',
  },
];

interface TrainingState {
  currentExercise: TrainingMode;
  eyeToTrain: EyeToTrain;
  isMonocular: boolean;
  isRedGreen: boolean;
  isMFBF: boolean;
  isOverlay: boolean;
  isDevMode: boolean;
  speed: number;
  range: RangeSetting;
  distance: DistanceSetting;
  selectedExercises: TrainingMode[];
  workoutConfig: WorkoutConfig | null;
  setCurrentExercise: (exercise: TrainingMode) => void;
  setEyeToTrain: (eye: EyeToTrain) => void;
  toggleMonocular: () => void;
  toggleRedGreen: () => void;
  toggleMFBF: () => void;
  toggleOverlay: () => void;
  toggleDevMode: () => void;
  setSpeed: (speed: number) => void;
  setRange: (range: RangeSetting) => void;
  setDistance: (distance: DistanceSetting) => void;
  applyPreset: (preset: PresetConfig) => void;
  toggleExerciseSelection: (exercise: TrainingMode) => void;
  clearSelectedExercises: () => void;
  setWorkoutConfig: (config: WorkoutConfig | null) => void;
  isTimerPaused: boolean;
  toggleTimerPause: (paused: boolean) => void;
}

export const useStore = create<TrainingState>()(set => ({
  currentExercise: 'convergence',
  eyeToTrain: 'both',
  isMonocular: false,
  isRedGreen: true,
  isMFBF: false,
  isOverlay: false,
  isDevMode: false,
  speed: 7,
  range: 'medium',
  distance: 'middle',
  selectedExercises: [],
  workoutConfig: null,
  setCurrentExercise: exercise => set({currentExercise: exercise}),
  setEyeToTrain: eye => set({eyeToTrain: eye}),
  toggleMonocular: () => set(state => ({isMonocular: !state.isMonocular})),
  toggleRedGreen: () => set(state => ({isRedGreen: !state.isRedGreen})),
  toggleMFBF: () => set(state => ({isMFBF: !state.isMFBF})),
  toggleOverlay: () => set(state => ({isOverlay: !state.isOverlay})),
  toggleDevMode: () => set(state => ({isDevMode: !state.isDevMode})),
  setSpeed: speed => set({speed}),
  setRange: range => set({range}),
  setDistance: distance => set({distance}),
  applyPreset: (preset: PresetConfig) =>
    set({
      currentExercise: preset.exercise,
      eyeToTrain: preset.eyeToTrain,
      isMonocular: preset.isMonocular,
      isRedGreen: preset.isRedGreen,
      isMFBF: preset.isMFBF,
      speed: preset.speed,
      range: preset.range,
      distance: preset.distance,
    }),
  toggleExerciseSelection: (exercise: TrainingMode) =>
    set(state => {
      const isSelected = state.selectedExercises.includes(exercise);
      const newSelected = isSelected
        ? state.selectedExercises.filter(e => e !== exercise)
        : [...state.selectedExercises, exercise];

      // Calculate workout config if exercises are selected
      const workoutConfig =
        newSelected.length > 0
          ? {
              exercises: newSelected.map(type => ({type, duration: 120 / newSelected.length})),
              totalDuration: 120,
            }
          : null;

      return {
        selectedExercises: newSelected,
        workoutConfig,
      };
    }),
  clearSelectedExercises: () => set({selectedExercises: [], workoutConfig: null}),
  setWorkoutConfig: (config: WorkoutConfig | null) => set({workoutConfig: config}),
  isTimerPaused: false,
  toggleTimerPause: paused => set({isTimerPaused: paused}),
}));
